import React from 'react';
import { Avatar, Box, SxProps } from '@mui/material';
import { getInitials } from '../utils/getInitials';
import { themeConstants } from '../theme';

export interface EntityImageProps {
    /** Alt text and initials source */
    name: string;
    src?: string | null;
    size?: number;
    shape?: 'square' | 'circle';
    variant?: 'default' | 'bordered' | 'plain';
    /** Override initials background (e.g. ENTITY_COLORS in compare headers) */
    placeholderColor?: string;
    sx?: SxProps;
    onClick?: React.MouseEventHandler;
}

export const EntityImage: React.FC<EntityImageProps> = ({
    name,
    src,
    size = 40,
    shape = 'square',
    variant = 'default',
    placeholderColor,
    sx,
    onClick,
}) => {
    const hasSrc = !!src?.trim();
    const isCircle = shape === 'circle';
    const fontSize = size * 0.35;
    const initials = getInitials(name);

    const placeholderBgColor = placeholderColor
        ?? (variant === 'bordered' ? 'surfaceMuted' : 'primary.main');
    const placeholderTextColor = placeholderColor
        ? 'common.white'
        : variant === 'bordered'
            ? 'primary.main'
            : 'primary.contrastText';

    const avatarSx: SxProps = {
        width: variant === 'bordered' ? '100%' : size,
        height: variant === 'bordered' ? '100%' : size,
        fontSize: variant === 'bordered' && size >= 96 ? '2.25rem' : fontSize,
        bgcolor: hasSrc ? 'background.paper' : placeholderBgColor,
        color: hasSrc ? 'primary.main' : placeholderTextColor,
        fontFamily: '"DM Serif Text", serif',
        fontWeight: 700,
        borderRadius: isCircle
            ? undefined
            : variant === 'bordered'
                ? 0
                : themeConstants.border.radius,
        flexShrink: 0,
        cursor: onClick ? 'pointer' : undefined,
        '& .MuiAvatar-img': {
            objectFit: 'contain',
        },
        ...(placeholderColor && !hasSrc
            ? {
                  border: '2px solid',
                  borderColor: placeholderColor,
              }
            : {}),
        ...sx,
    };

    const avatar = (
        <Avatar
            src={hasSrc ? src! : undefined}
            alt={name}
            variant={isCircle ? 'circular' : 'rounded'}
            sx={avatarSx}
            onClick={onClick}
        >
            {initials}
        </Avatar>
    );

    if (variant === 'bordered') {
        return (
            <Box
                sx={{
                    width: size,
                    height: size,
                    flexShrink: 0,
                    borderRadius: themeConstants.border.radius,
                    border: `${themeConstants.border.width} solid`,
                    borderColor: 'divider',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                    bgcolor: hasSrc ? 'background.paper' : 'surfaceMuted',
                    ...sx,
                }}
            >
                {avatar}
            </Box>
        );
    }

    return avatar;
};
